import React, { useState } from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native-web';
import Input from '../components/Input';
import Button from '../components/Button';
import { colors, radius, shadows } from '../theme';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      if (!email || !password) {
        setError('Email and password are required');
        setLoading(false);
        return;
      }

      // Call Keycloak token endpoint
      const tokenResponse = await fetch(
        `${process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:9090'}/realms/fbmirror/protocol/openid-connect/token`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'password',
            client_id: 'fbmirror-frontend',
            username: email,
            password: password,
          }),
        }
      );

      if (!tokenResponse.ok) {
        const errorData = await tokenResponse.json();
        setError(errorData.error_description || 'Login failed');
        setLoading(false);
        return;
      }

      const data = await tokenResponse.json();

      // Store token in localStorage
      localStorage.setItem('keycloak_token', data.access_token);
      localStorage.setItem('keycloak_refresh_token', data.refresh_token);

      // Redirect to dashboard
      window.location.href = '/';
    } catch (e) {
      setError('Login error: ' + (e.message || 'Unknown error'));
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={styles.header}>
          <Text style={styles.logo}>📊</Text>
          <Text style={styles.title}>FB Pixel Mirror</Text>
          <Text style={styles.subtitle}>Event Forwarding & Analytics</Text>
        </View>

        <View style={styles.form}>
          <Input
            label="Email"
            value={email}
            onChangeText={setEmail}
            placeholder="you@example.com"
            onKeyPress={handleKeyPress}
          />

          <Input
            label="Password"
            value={password}
            onChangeText={setPassword}
            placeholder="••••••••"
            secureTextEntry
            onKeyPress={handleKeyPress}
          />

          {error && (
            <View style={styles.errorBox}>
              <Text style={styles.errorText}>{error}</Text>
            </View>
          )}

          <Button
            label={loading ? 'Signing in...' : 'Sign In'}
            onPress={handleLogin}
            variant="primary"
            style={styles.submitBtn}
          />
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>FB Pixel Mirror</Text>
          <Text style={styles.footerCode}>Contact your administrator for access</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.bg,
    minHeight: '100vh',
    padding: 16,
  },
  card: {
    backgroundColor: colors.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: 40,
    width: '100%',
    maxWidth: 420,
    boxShadow: shadows.card,
  },
  header: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logo: {
    fontSize: 48,
    marginBottom: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 13,
    color: colors.textSecondary,
    letterSpacing: 0.3,
  },
  form: {
    marginBottom: 24,
  },
  errorBox: {
    backgroundColor: 'rgba(239,68,68,0.1)',
    borderWidth: 1,
    borderColor: colors.danger,
    borderRadius: radius.sm,
    padding: 12,
    marginBottom: 16,
  },
  errorText: {
    color: colors.danger,
    fontSize: 13,
    fontWeight: '500',
  },
  submitBtn: {
    marginTop: 8,
  },
  footer: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: radius.sm,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  footerText: {
    fontSize: 11,
    fontWeight: '600',
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  footerCode: {
    fontFamily: 'monospace',
    fontSize: 12,
    color: colors.success,
    marginBottom: 4,
  },
});
